/**
 * Branch List Component
 * Fallback list view for users who cannot view the map
 */

import { MapPin, Phone, Clock, Navigation, Store } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import type { Branch } from '../stores/branch-store';

interface BranchListProps {
  branches: Branch[];
  className?: string;
}

export function BranchList({ branches, className = '' }: BranchListProps) {
  return (
    <div className={`grid gap-4 md:grid-cols-2 lg:grid-cols-3 ${className}`}>
      {branches.map((branch) => (
        <Card 
          key={branch.id} 
          className="hover:shadow-md transition-shadow"
        >
          <CardContent className="p-5">
            {/* Branch Header */}
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="p-2 bg-brand-100 rounded-full">
                  <Store className="w-5 h-5 text-brand-600" />
                </div>
                <div>
                  <h3 className="font-bold text-brand-900">{branch.name}</h3>
                  {branch.is_main && (
                    <Badge className="bg-brand-600 text-white text-xs mt-1">
                      Main Branch
                    </Badge>
                  )}
                </div>
              </div>
            </div>

            {/* Branch Details */}
            <div className="space-y-2.5">
              <p className="text-brand-700 flex items-start gap-2 text-sm">
                <MapPin className="w-4 h-4 text-brand-500 mt-0.5 flex-shrink-0" />
                <span>{branch.location}</span>
              </p>
              
              {branch.phone && (
                <p className="text-brand-700 flex items-center gap-2 text-sm">
                  <Phone className="w-4 h-4 text-brand-500 flex-shrink-0" />
                  <a 
                    href={`tel:${branch.phone.replace(/\s+/g, '')}`}
                    className="hover:text-brand-600 hover:underline"
                  >
                    {branch.phone}
                  </a>
                </p>
              )}
              
              {branch.operating_hours ? (
                <p className="text-brand-700 flex items-center gap-2 text-sm">
                  <Clock className="w-4 h-4 text-brand-500 flex-shrink-0" />
                  <span>
                    Today: {branch.operating_hours.open} - {branch.operating_hours.close}
                  </span>
                </p>
              ) : (
                <p className="text-brand-700 flex items-center gap-2 text-sm">
                  <Clock className="w-4 h-4 text-brand-500 flex-shrink-0" />
                  <span>Hours not available</span>
                </p>
              )}

              {branch.mpesa_shortcode && (
                <p className="text-brand-600 text-sm bg-brand-50 px-3 py-1.5 rounded">
                  <span className="font-medium">M-PESA Till:</span> {branch.mpesa_shortcode}
                </p>
              )}
            </div>

            {/* Actions */}
            <div className="mt-4 pt-4 border-t border-brand-100 flex gap-2">
              {branch.phone && (
                <Button size="sm" variant="outline" asChild className="flex-1">
                  <a href={`tel:${branch.phone.replace(/\s+/g, '')}`}>
                    <Phone className="w-4 h-4 mr-1" />
                    Call
                  </a>
                </Button>
              )}
              {branch.lat && branch.lng && (
                <Button size="sm" asChild className="flex-1 bg-brand-600 hover:bg-brand-700">
                  <a 
                    href={`https://www.google.com/maps/dir/?api=1&destination=${branch.lat},${branch.lng}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Navigation className="w-4 h-4 mr-1" />
                    Directions
                  </a>
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

interface BranchListSkeletonProps {
  className?: string;
}

export function BranchListSkeleton({ className = '' }: BranchListSkeletonProps) {
  return (
    <div className={`grid gap-4 md:grid-cols-2 lg:grid-cols-3 ${className}`}>
      {Array.from({ length: 3 }).map((_, i) => (
        <Card key={i}>
          <CardContent className="p-5">
            <div className="animate-pulse">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-10 h-10 bg-brand-100 rounded-full" />
                <div>
                  <div className="h-4 w-32 bg-brand-100 rounded" />
                  <div className="h-3 w-16 bg-brand-100 rounded mt-2" />
                </div>
              </div>
              <div className="space-y-2">
                <div className="h-4 w-full bg-brand-100 rounded" />
                <div className="h-4 w-3/4 bg-brand-100 rounded" />
                <div className="h-4 w-1/2 bg-brand-100 rounded" />
              </div>
              <div className="mt-4 pt-4 border-t border-brand-100 flex gap-2">
                <div className="h-9 flex-1 bg-brand-100 rounded" />
                <div className="h-9 flex-1 bg-brand-100 rounded" />
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
